import { ErrorList } from 'domains/models';
import { Request } from 'express';
import axios, { AxiosResponse, AxiosError } from 'axios';
import * as Sentry from '@sentry/nextjs';
import { setEvent } from 'presentations/views/helpers/analytics';

const headers: any = {};
if (process.env['NODE_ENV'] !== 'production') {
  headers['Access-Control-Allow-Origin'] = '*';
}

const config: any = {
  baseURL: process.env['JICOO_API_HOST'],
  timeout: 30 * 1000,
  responseType: 'json',
  headers,
};

// if (
//   process.env['JICOO_BASIC_AUTH_USER'] &&
//   process.env['JICOO_BASIC_AUTH_PASS']
// ) {
//   config['auth'] = {
//     username: process.env['JICOO_BASIC_AUTH_USER']!,
//     password: process.env['JICOO_BASIC_AUTH_PASS']!,
//   };
// }

const restClient = axios.create(config);

export interface RestClientResponse {
  isSuccess: boolean;
  data: any | null;
  errors: ErrorList;
  errorMessage?: string | null;
  status: number;
  headers?: any;
  count?: number;
  prevPage?: number;
  currentPage?: number;
  nextPage?: number;
  numberOfPages?: number;
  totalCount?: number;
  facets?: any;
}

export interface GetOptions {
  params?: any;
  headers?: any;
  timeout?: number;
  responseType?: 'json' | 'arraybuffer';
}
interface PostOptions {
  params?: any;
  data?: any;
  headers?: any;
  timeout?: number;
}
interface PatchOptions {
  params?: any;
  data?: any;
}
interface DestroyOptions {
  params?: any;
  data?: any;
  headers?: any;
}

interface HandleErrorParams {
  url: string;
  error: AxiosError;
  method: string;
}

function serverSideHeaders(req: Request) {
  const headers: any = {};
  if (req.cookies.session) {
    headers['Authorization'] = `Bearer ${req.cookies.session}`;
  }
  if (req.cookies.team) {
    headers['X-Jicoo-Team'] = req.cookies.team;
  }
  if (req.cookies.team_version) {
    headers['X-Jicoo-Team-Version'] = req.cookies.team_version;
  }
  if (req.cookies.supportteam) {
    headers['X-Jicoo-Supportteam'] = req.cookies.supportteam;
  }
  if (req.cookies.supportuid) {
    headers['X-Jicoo-Supportuid'] = req.cookies.supportuid;
  }
  if (process.env['NODE_ENV'] === 'production') {
    headers['x-forwarded-for'] = req.headers['x-forwarded-for'];
  }
  return headers;
}

export async function serverSideGet(
  req: Request,
  url: string,
  options: GetOptions | null = null,
) {
  const headers = serverSideHeaders(req);
  const newOptions = {
    ...options,
    headers,
  };
  return get(url, newOptions);
}

export async function get(
  url: string,
  options: GetOptions | null = null,
): Promise<RestClientResponse> {
  let params;
  let headers;
  let responseType;
  let timeout;
  if (options) {
    params = options.params;
    headers = options.headers;
    responseType = options.responseType;
    timeout = options.timeout;
  }
  const method = 'get';
  try {
    const res: AxiosResponse = await restClient.request({
      method,
      url,
      responseType,
      params: params,
      headers: headers,
      timeout,
      withCredentials: true,
    });

    if (responseType === 'arraybuffer') {
      return {
        isSuccess: true,
        data: res.data,
        status: res.status,
        errors: {},
      };
    }

    const result = res.data;
    return {
      isSuccess: true,
      data: result.data,
      errors: result.errors || {},
      status: res.status,
      headers: res.headers,
      count: result.count,
      currentPage: result.currentPage,
      prevPage: result.prevPage,
      nextPage: result.nextPage,
      numberOfPages: result.numberOfPages,
      totalCount: result.totalCount,
      facets: result.facets,
    };
  } catch (error) {
    console.log(`Failed to get ${url}`);
    return handleError({ url, error, method });
  }
}

export async function serverSidePatch(
  req: Request,
  url: string,
  options: GetOptions | null = null,
) {
  const headers = serverSideHeaders(req);
  const newOptions = {
    ...options,
    headers,
  };
  return patch(url, newOptions);
}

export async function patch(
  url: string,
  options: PatchOptions | null = null,
): Promise<RestClientResponse> {
  let data: any = {};
  if (options) {
    if (options.data) {
      data = options.data;
    }
  }
  const method = 'patch';
  try {
    const res: AxiosResponse = await restClient.request({
      method,
      url,
      data,
      params: options?.params,
      headers: {},
      withCredentials: true,
    });
    const result = res.data;
    return {
      isSuccess: true,
      data: result.data,
      status: res.status,
      headers: res.headers,
      errors: result.errors || {},
    };
  } catch (error) {
    console.log(`Failed to patch ${url}`);
    return handleError({ url, method, error });
  }
}

export async function serverSideDestroy(
  req: Request,
  url: string,
  options: GetOptions | null = null,
) {
  const headers = serverSideHeaders(req);
  console.log(headers);
  const newOptions = {
    ...options,
    headers,
  };
  return destroy(url, newOptions);
}

export async function destroy(
  url: string,
  options: DestroyOptions | null = null,
): Promise<RestClientResponse> {
  let data: any = {};
  let headers: any = {};
  if (options) {
    if (options.data) {
      data = options.data;
    }
    headers = options.headers;
  }
  const method = 'delete';
  try {
    const res: AxiosResponse = await restClient.request({
      method,
      url,
      data,
      headers,
      withCredentials: true,
    });
    const result = res.data;
    return {
      isSuccess: true,
      data: result.data,
      status: res.status,
      headers: res.headers,
      errors: result.errors || {},
    };
  } catch (error) {
    console.log(`Failed to destroy ${url}`);
    return handleError({ url, method, error });
  }
}

export async function serverSidePost(
  req: Request,
  url: string,
  options: PostOptions,
) {
  const headers = serverSideHeaders(req);
  const newOptions = {
    ...options,
    headers,
  };
  return post(url, newOptions);
}

export async function post(
  url: string,
  options: PostOptions | null = null,
): Promise<RestClientResponse> {
  const method = 'post';
  try {
    const res: AxiosResponse = await restClient.request({
      method,
      url,
      data: options?.data,
      headers: options?.headers,
      params: options?.params,
      timeout: options?.timeout,
      withCredentials: true,
      maxBodyLength: 30 * 1024 * 1024, // 30MB
      maxContentLength: 30 * 1024 * 1024, // 30MB
    });
    const result = res.data;
    return {
      isSuccess: true,
      data: result.data,
      status: res.status,
      headers: res.headers,
      errors: result.errors || {},
    };
  } catch (error) {
    console.log(`Failed to post ${url}`);
    return handleError({ url, method, error });
  }
}

export function handleError(params: HandleErrorParams): RestClientResponse {
  const { url, method, error } = params;
  let data = null;
  let status = 0;
  let errorMessage = null;
  let errors: ErrorList = {};

  if (error.message) {
    errorMessage = error.message;
  }
  if (error.response) {
    const res = error.response;
    const result = res.data;
    status = res.status;
    if (result) {
      if (result.data) {
        data = result.data;
      }
      if (result.errors) {
        errors = result.errors;
      }
      if (result.errorMessage) {
        errorMessage = result.errorMessage;
      }
    }
  } else if (error.request) {
    console.debug(error.request.response);
  } else {
    console.debug(
      'Something happened in setting up the request that triggered an Error',
    );
  }

  if (status && Number(status) >= 500 && typeof window !== 'undefined') {
    setEvent(
      'jicoo api error',
      {
        status,
        requestUrl: url,
        method,
        errorMessage,
        errors,
        data,
      },
      {
        isAmplitude: true,
      },
    );
    Sentry.captureEvent({
      message: `RestClientError ${method.toUpperCase()} ${url}`,
      level: 'error',
    });
  }

  return {
    data,
    errorMessage,
    status,
    errors: errors || {},
    isSuccess: false,
  };
}
